import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import BrandLogo from "../components/BrandLogo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { INQUIRY_QUESTIONS, BRAND } from "../mock";
import { draftStore, submitLead } from "../lib/api";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const URL_RX = /^https?:\/\/[^\s./]+\.[^\s]{2,}$/i;

function isQuestionValid(question, value) {
  if (!question.required) return { ok: true };
  if (!value || String(value).trim() === "") {
    return { ok: false, message: "This field is required" };
  }
  if (question.type === "email" && !EMAIL_RX.test(value)) {
    return { ok: false, message: "Please enter a valid email" };
  }
  if (question.type === "url" && !URL_RX.test(String(value).trim())) {
    return { ok: false, message: "Please enter a valid link." };
  }
  return { ok: true };
}

function getNextButtonLabel(isLastStep, submitting) {
  if (!isLastStep) return "Next";
  if (submitting) return "Sending...";
  return "Submit";
}

function sanitizePhone(raw) {
  const phone = (raw || "").trim();
  const digitsOnly = phone.replace(/[^0-9]/g, "");
  return digitsOnly.length < 5 ? "" : phone;
}

export default function Inquiry() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [answers, setAnswers] = useState(() => {
    const draft = draftStore.getDraft();
    const leadEmail = draftStore.getLeadEmail();
    return { email: leadEmail, ...draft };
  });

  const q = INQUIRY_QUESTIONS[step];
  const total = INQUIRY_QUESTIONS.length;
  const progress = useMemo(
    () => Math.round(((step + 1) / total) * 100),
    [step, total]
  );

  useEffect(() => {
    draftStore.saveDraft(answers);
  }, [answers]);

  const setVal = (val) => setAnswers((a) => ({ ...a, [q.id]: val }));

  const validate = () => {
    const result = isQuestionValid(q, answers[q.id]);
    if (!result.ok) {
      toast.error(result.message);
      return false;
    }
    return true;
  };

  const next = () => {
    if (!validate()) return;
    if (step < total - 1) setStep(step + 1);
    else handleSubmit();
  };
  const back = () => setStep(Math.max(0, step - 1));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        name: answers.name || "",
        email: answers.email || "",
        phone: sanitizePhone(answers.phone),
        musicLink: answers.musicLink || "",
        timeframe: answers.timeframe || "",
        holdup: answers.holdup || "",
        project: "",
        goals: "",
        anythingElse: "",
      };
      await submitLead(payload);
      draftStore.clearDraft();
      toast.success("Inquiry sent! Booking your call next.");
      setTimeout(() => navigate("/schedule"), 400);
    } catch (e) {
      const msg =
        e?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = () => {
    const val = answers[q.id] || "";
    if (q.id === "phone") {
      return (
        <div className="phone-field">
          <PhoneInput
            defaultCountry="gb"
            value={val}
            onChange={(phone) => setVal(phone)}
            inputClassName="!w-full !h-14 !text-base !rounded-r-md !border-gray-300 !border-l-0 focus:!ring-2 focus:!ring-paul-yellow"
            countrySelectorStyleProps={{
              buttonClassName:
                "!h-14 !rounded-l-md !border-gray-300 !bg-white !px-3",
              dropdownStyleProps: {
                className: "!max-h-72 !rounded-lg !shadow-xl !border-gray-200",
              },
            }}
            className="w-full"
          />
          <p className="mt-3 text-xs text-gray-500">
            Tip: tap the flag to switch country.
          </p>
        </div>
      );
    }
    if (q.type === "textarea") {
      return (
        <Textarea
          autoFocus
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder={q.placeholder}
          className="min-h-[160px] text-base"
        />
      );
    }
    if (q.type === "radio") {
      return (
        <div className="space-y-3">
          {q.options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => setVal(opt)}
              className={`w-full text-left px-5 py-4 rounded-lg border-2 transition-all ${
                val === opt
                  ? "border-paul-yellow bg-yellow-50"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    val === opt
                      ? "border-paul-yellow bg-paul-yellow"
                      : "border-gray-300"
                  }`}
                >
                  {val === opt && <Check className="w-3 h-3 text-white" />}
                </div>
                <span className="text-navy font-medium">{opt}</span>
              </div>
            </button>
          ))}
        </div>
      );
    }
    return (
      <Input
        autoFocus
        type={q.type}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder={q.placeholder}
        onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && next()}
        className="h-14 text-base"
      />
    );
  };

  return (
    <div className="dark-site min-h-screen bg-soft flex flex-col">
      <header className="py-5 px-6 border-b border-gray-200 bg-white">
        <div className="max-w-3xl mx-auto flex items-center justify-center">
          <button
            onClick={() => navigate("/")}
            className="transition-opacity hover:opacity-70"
            aria-label="Paul Vilas — home"
          >
            <BrandLogo variant="yellow" className="h-9" />
          </button>
        </div>
        <div className="max-w-3xl mx-auto mt-3">
          <Progress value={progress} className="h-1" />
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center justify-start px-6 pt-12 md:pt-16 pb-12">
        <h2 className="text-3xl md:text-4xl font-extrabold text-navy mb-10 md:mb-12">
          2 Questions
        </h2>

        <div className="w-full max-w-2xl fade-in" key={step}>
          <h1 className="text-2xl md:text-3xl font-extrabold text-navy leading-tight text-center">
            {q.label}
          </h1>
          {q.helper && (
            <p className="mt-3 text-gray-500 text-center">{q.helper}</p>
          )}
          <div className="mt-8">{renderField()}</div>

          <div className="mt-10 flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={back}
              disabled={step === 0}
              className="text-navy disabled:opacity-30"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button
              onClick={next}
              disabled={submitting}
              className="bg-paul-yellow hover:bg-paul-yellow text-navy font-bold px-8 py-6"
              style={{ color: "#0b1535" }}
            >
              {getNextButtonLabel(step === total - 1, submitting)}
              {step !== total - 1 && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          </div>
          <p className="mt-6 text-xs text-gray-400 text-center">
            Your answers are saved automatically.
          </p>
        </div>
      </main>
    </div>
  );
}
